import React, { useContext, useState } from 'react';
import {
  TinyTitle,
  Body,
  palettes,
  ExtraLargeTitle,
  Button,
  DatePicker,
  useFlashMessage,
  Divider,
  CollapsibleContentCard
} from '@indigo-ag/component-module';
import { AgFinancing, AgOpportunity } from '@indigo-ag/schema';
import i18next from 'core/i18n';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { MaterialUiPickersDate } from 'material-ui-pickers/typings/date';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';

import { createStylesHook, utils, Permissions, CURRENCY_SYMBOL, date, transformArrayToDictionary } from 'shared';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { UserContext } from 'core/components/GateWay/GateWay';
import { upsertFinancingAgFinancing } from 'features/brazil-grower/queries';
import styled from 'styled-components';

const useStyles = createStylesHook({
  containerBox: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 40
  },
  recordBox: {
    marginBottom: 30,
    '& p': {
      marginTop: 2
    },
    '& h6': {
      color: palettes.neutral['600']
    }
  },
  list: {
    margin: 0,
    paddingLeft: 18,
    marginTop: 5
  }
});

const OrderCollapsibleContentCard = styled(CollapsibleContentCard)`
  margin-top: 16px;
`;
const OrderDetailContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 16px;
`;
const OrderDetailItem = styled.div`
  width: 250px;
  margin-bottom: 16px;
  padding-right: 16px;
  > h6 {
    color: #757575;
  }
`;

interface OrderDetailProps {
  orderInfo: AgFinancing['orderInfo'];
  isOrderDetailsCard?: boolean | undefined;
}

export const OrderDetail: React.FunctionComponent<OrderDetailProps> = ({ orderInfo, isOrderDetailsCard }) => {
  /**
   * FinanceProcessContext
   */
  const { baseRoute, agFinance } = useContext(FinanceProcessContext);

  /**
   * UserContext
   */
  const { hasPermission } = useContext(UserContext);

  /**
   * History
   */
  const history = useHistory();

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Context's
   */
  const { collateralType, opportunityType, opportunitySaleType } = useContext(FinanceProcessContext);

  /**
   * state
   */
  const [editingOpportunityId, setEditingOpportunityId] = useState<AgOpportunity['id']>(undefined);
  const [stateOpportunities, setStateOpportunities] = useState(orderInfo?.opportunities);
  /**
   * mutation
   */
  const [upsertAgFinancingFn, { loading }] = useMutation(upsertFinancingAgFinancing);

  /**
   * callbacks
   */
  const handleDateChange = (date: MaterialUiPickersDate, id: AgOpportunity['id'] = '') => {
    if (stateOpportunities?.length) {
      const dictionary = transformArrayToDictionary(stateOpportunities, 'id');
      const opportunity = dictionary[id];
      if (opportunity) {
        dictionary[id] = { ...opportunity, collateralDueDate: date };
        setStateOpportunities(Object.values(dictionary));
      }
    }
  };

  const { addFlashMessage } = useFlashMessage();

  const handleSaveOrderInfo = () =>
    upsertAgFinancingFn({
      variables: {
        data: {
          id: agFinance?.id,
          orderInfo: {
            opportunities: stateOpportunities?.map(({ id, collateralDueDate }) => ({ id, collateralDueDate }))
          }
        }
      }
    })
      .then(() => {
        setEditingOpportunityId(undefined);
        addFlashMessage({
          content: i18next.t('dateSaved'),
          title: '',
          variant: 'success'
        });
      })
      .catch(() =>
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        })
      );

  const handleDetailOrder = () => {
    history.push(`${baseRoute}/order`);
  };
  const isInternal = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);
  const isLuchesi = hasPermission(Permissions.FA_EXTERNAL_LEGAL);
  const isCreditManager = hasPermission(Permissions.FA_CREDIT_LEAD);
  const contractValue = orderInfo?.opportunities?.reduce((acc, curVal) => acc + Number(curVal?.contractValue), 0) || 0;

  const minutaHasBeenSent = !!agFinance?.phase3?.draftSentAt;
  const shouldDisableEditCollateral = (!isInternal && !isLuchesi) || minutaHasBeenSent;

  if (isOrderDetailsCard) {
    return (
      <OrderCollapsibleContentCard
        titleText={i18next.t('orderDetail')}
        initialCollapse
        footerActions={[
          { kind: 'Button', onClick: () => handleDetailOrder(), variant: 'secondary', label: i18next.t('seeFullOrder') }
        ]}
      >
        <OrderDetailContainer>
          {!!orderInfo?.opportunities?.length ? (
            <>
              {(isLuchesi || isCreditManager || isInternal) && (
                <>
                  <OrderDetailItem>
                    <TinyTitle>{i18next.t('cvExpirationDate')}</TinyTitle>
                    <Body letMeMargin>
                      {utils.date.format({ date: orderInfo.opportunities[0]?.dueDate, useUTC: true })}
                    </Body>
                  </OrderDetailItem>
                  <OrderDetailItem>
                    <TinyTitle>{i18next.t('contractValue')}</TinyTitle>
                    <Body letMeMargin>
                      {utils.currency.inputMaskCurrency(String(contractValue), {
                        currencySymbol: CURRENCY_SYMBOL.BR,
                        noValueCharacter: '-',
                        useComaForDecimals: true
                      })}
                    </Body>
                  </OrderDetailItem>
                </>
              )}
              <OrderDetailItem>
                <TinyTitle>{utils.text.toTitleCase(i18next.t('preCreditLimitApproved'))}</TinyTitle>
                <Body letMeMargin>{orderInfo.opportunities[0]?.preCreditLimitApproved || ' - '}</Body>
              </OrderDetailItem>
              <OrderDetailItem>
                <TinyTitle>{i18next.t('type')}</TinyTitle>
                <Body letMeMargin>{opportunityType ? i18next.t(opportunityType) : ' - '}</Body>
              </OrderDetailItem>
              <OrderDetailItem>
                <TinyTitle>{i18next.t('warrantyType')}</TinyTitle>
                <Body letMeMargin>{collateralType ? i18next.t(collateralType) : ' - '}</Body>
              </OrderDetailItem>
              <OrderDetailItem>
                <TinyTitle>{i18next.t('saleType')}</TinyTitle>
                <Body letMeMargin>{opportunitySaleType ? i18next.t(opportunitySaleType) : ' - '}</Body>
              </OrderDetailItem>
              <OrderDetailItem>
                <TinyTitle>{i18next.t('campaign')}</TinyTitle>
                <Body letMeMargin>{orderInfo.campaign}</Body>
              </OrderDetailItem>
              <OrderDetailItem>
                <TinyTitle>{i18next.t('products')}</TinyTitle>
                {orderInfo.opportunities[0].products.length ? (
                  <Body letMeMargin>{orderInfo.opportunities[0].products.join(', ')}</Body>
                ) : (
                  <Body letMeMargin> - </Body>
                )}
              </OrderDetailItem>
              {!!stateOpportunities?.length &&
                stateOpportunities.map(({ id, tradingName, collateralDueDate }, index) => {
                  const opportunityLabel =
                    stateOpportunities.length > 1
                      ? `${i18next.t('opportunityName')} ${index + 1}`
                      : i18next.t('opportunityName');
                  const dueDateLabel =
                    stateOpportunities.length > 1
                      ? `${i18next.t('collateralDueDate')} ${index + 1}`
                      : i18next.t('collateralDueDate');
                  return id ? (
                    <div key={id}>
                      <OrderDetailItem>
                        <TinyTitle>{opportunityLabel}</TinyTitle>
                        <Body letMeMargin style={{ marginBottom: '10px' }}>
                          {tradingName ?? '-'}
                        </Body>
                      </OrderDetailItem>
                      <OrderDetailItem>
                        {editingOpportunityId === id ? (
                          <>
                            <DatePicker
                              id="collateralDueDate"
                              label={dueDateLabel}
                              onChange={value => handleDateChange(value, id)}
                              sizeVariant="slim"
                              value={collateralDueDate || null}
                            />
                            <div className="flex align-items-center flex-space-btw">
                              <Button
                                onClick={() => setEditingOpportunityId(undefined)}
                                variant="secondary"
                                size="small"
                              >
                                {i18next.t('cancel')}
                              </Button>
                              <Button
                                onClick={handleSaveOrderInfo}
                                size="small"
                                busy={loading}
                                disabled={!collateralDueDate}
                              >
                                {i18next.t('save')}
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <TinyTitle>{dueDateLabel}</TinyTitle>
                            <div className="flex flex-space-btw">
                              <Body letMeMargin style={{ paddingTop: '7px' }}>
                                {collateralDueDate ? date.format({ date: collateralDueDate }) : ' - '}
                              </Body>
                              <Button
                                faIcon={faPencil}
                                variant="tertiary"
                                onClick={() => setEditingOpportunityId(id)}
                                disabled={shouldDisableEditCollateral}
                                size="small"
                                faIconColor={palettes.brand.gray[600]}
                              />
                            </div>
                          </>
                        )}
                      </OrderDetailItem>
                    </div>
                  ) : null;
                })}
            </>
          ) : (
            <Body letMeMargin>{i18next.t('noOrderFound')}</Body>
          )}
        </OrderDetailContainer>
      </OrderCollapsibleContentCard>
    );
  }

  return (
    <div className={classes.containerBox}>
      <ExtraLargeTitle style={{ marginBottom: 30, fontWeight: 300 }}>{i18next.t('order')}</ExtraLargeTitle>
      {!!orderInfo?.opportunities?.length ? (
        <>
          {(isLuchesi || isCreditManager || isInternal) && (
            <>
              <div className={classes.recordBox}>
                <TinyTitle>{i18next.t('cvExpirationDate')}</TinyTitle>
                <Body letMeMargin>
                  {utils.date.format({ date: orderInfo.opportunities[0]?.dueDate, useUTC: true })}
                </Body>
              </div>
              <div className={classes.recordBox}>
                <TinyTitle>{i18next.t('contractValue')}</TinyTitle>
                <Body letMeMargin>
                  {utils.currency.inputMaskCurrency(String(contractValue), {
                    currencySymbol: CURRENCY_SYMBOL.BR,
                    noValueCharacter: '-',
                    useComaForDecimals: true
                  })}
                </Body>
              </div>
            </>
          )}
          {!!stateOpportunities?.length &&
            stateOpportunities.map(({ id, tradingName, collateralDueDate }, index) =>
              id ? (
                <div key={index} className={classes.recordBox}>
                  <TinyTitle>{i18next.t('opportunityName')}</TinyTitle>
                  <Body letMeMargin style={{ marginBottom: '10px' }}>
                    {tradingName}
                  </Body>
                  {editingOpportunityId === id ? (
                    <>
                      <DatePicker
                        id="collateralDueDate"
                        label={i18next.t('collateralDueDate')}
                        onChange={value => handleDateChange(value, id)}
                        sizeVariant="slim"
                        value={collateralDueDate || null}
                      />
                      <div className="flex align-items-center flex-space-btw">
                        <Button onClick={() => setEditingOpportunityId(undefined)} variant="secondary" size="small">
                          {i18next.t('cancel')}
                        </Button>
                        <Button onClick={handleSaveOrderInfo} size="small" busy={loading} disabled={!collateralDueDate}>
                          {i18next.t('save')}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <TinyTitle>{i18next.t('collateralDueDate')}</TinyTitle>
                      <div className="flex flex-space-btw">
                        <Body letMeMargin style={{ paddingTop: '7px' }}>
                          {collateralDueDate ? date.format({ date: collateralDueDate }) : ' - '}
                        </Body>
                        <Button
                          faIcon={faPencil}
                          variant="tertiary"
                          onClick={() => setEditingOpportunityId(id)}
                          disabled={shouldDisableEditCollateral}
                          size="small"
                          faIconColor={palettes.brand.gray[600]}
                        />
                      </div>
                    </>
                  )}
                  {stateOpportunities?.length > 1 && <Divider />}
                </div>
              ) : null
            )}
          <div className={classes.recordBox}>
            <TinyTitle>{utils.text.toTitleCase(i18next.t('preCreditLimitApproved'))}</TinyTitle>
            <Body letMeMargin>{orderInfo.opportunities[0]?.preCreditLimitApproved || ' - '}</Body>
          </div>
          <div className={classes.recordBox}>
            <TinyTitle>{i18next.t('type')}</TinyTitle>
            <Body letMeMargin>{opportunityType ? i18next.t(opportunityType) : ' - '}</Body>
          </div>
          <div className={classes.recordBox}>
            <TinyTitle>{i18next.t('warrantyType')}</TinyTitle>
            <Body letMeMargin>{collateralType ? i18next.t(collateralType) : ' - '}</Body>
          </div>
          <div className={classes.recordBox}>
            <TinyTitle>{i18next.t('saleType')}</TinyTitle>
            <Body letMeMargin>{opportunitySaleType ? i18next.t(opportunitySaleType) : ' - '}</Body>
          </div>
          <div className={classes.recordBox}>
            <TinyTitle>{i18next.t('campaign')}</TinyTitle>
            <Body letMeMargin>{orderInfo.campaign}</Body>
          </div>
          <div className={classes.recordBox}>
            <TinyTitle>{i18next.t('products')}</TinyTitle>
            {orderInfo.opportunities[0].products ? (
              <ul className={classes.list}>
                {orderInfo.opportunities[0].products.map((p, i) => (
                  <li key={i}>{p.productName}</li>
                ))}
              </ul>
            ) : (
              <Body letMeMargin>' - '</Body>
            )}
          </div>
          <Button size="small" variant="secondary" onClick={handleDetailOrder}>
            {i18next.t('seeFullOrder')}
          </Button>
        </>
      ) : (
        <Body letMeMargin>{i18next.t('noOrderFound')}</Body>
      )}
    </div>
  );
};
